import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "primereact/button";
import _ from "lodash";
import moment from "moment";

import "./tracking-information.scss";
import markerPurple from "../../assets/img/marker-purple.svg";
import markerGreen from "../../assets/img/marker-green.svg";
import riderImg from "../../assets/img/rider-img.svg";
import refresh from "../../assets/img/refresh.svg";
import ViewDetails from "./ViewDetails";
import ReactGA from "react-ga4";
import {
  pickup_statuses,
  trip_between_status,
  trip_final_status,
} from "../../pages/NewHomePage/utils";

import { MapContainer, Popup, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css"; // Import Leaflet CSS
import "leaflet-providers/leaflet-providers.js"; // Import Leaflet Providers
import "leaflet-routing-machine";
import L, { DivIcon } from "leaflet";
import { Marker } from "react-leaflet/Marker";
import theme from "../../services/theme";
import { LeafletTrackingMarker } from "react-leaflet-tracking-marker";
import ImageLinks from "../../services/ImageLinks";
import Text from "../common/Text";

const MovingMarker = ({ icon, data }) => {
  const { lat, lng } = data;
  const [prevPos, setPrevPos] = useState([lat, lng]);

  useEffect(() => {
    if (prevPos[1] !== lng && prevPos[0] !== lat) setPrevPos([lat, lng]);
  }, [lat, lng, prevPos]);

  return (
    <LeafletTrackingMarker
      icon={icon}
      position={[lat, lng]}
      previousPosition={prevPos}
      duration={1000}
    ></LeafletTrackingMarker>
  );
};

const TrackingDetails = ({
  deliveryInformation,
  rider_data,
  riderId,
  refreshRiderLocationUsingId,
  isLoading,
  buttonDisabled,
  configs,
  isPickupFlow,
  isReverse
}) => {
  const [activeIndex, setActiveIndex] = useState([]);
  const [map_loaded, set_map_loaded] = useState(false);
  const map_ref = useRef(null);
  const waypoint_ref = useRef(null);

  const onClick = (itemIndex) => {
    ReactGA.event({
      category: "User",
      action: "Logs Viewed",
    });
    let _activeIndex = activeIndex ? [...activeIndex] : [];

    if (_activeIndex.length === 0) {
      _activeIndex.push(itemIndex);
    } else {
      const index = _activeIndex.indexOf(itemIndex);

      if (index === -1) _activeIndex.push(itemIndex);
      else _activeIndex.splice(index, 1);
    }

    setActiveIndex(_activeIndex);
  };

  useEffect(() => {
    if (map_loaded && map_ref.current) {
      // calculate_bounds();
      construct_snap_to_road();
    }
  }, [map_loaded]);

  useEffect(() => {
    const status = _.get(deliveryInformation, 'fulfillment.status', '');

    const validStatuses = ['CREATED', 'OUT_FOR_PICKUP', 'REACHED_PICKUP'];

    if (!isReverse || !validStatuses.includes(status)) {
      return;
    }

    const timer = setInterval(() => {
      refreshRiderLocationUsingId(riderId, true);
    }, 30000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const calculate_bounds = () => {
    const markers_bound = [];
    if (!_.isEmpty(rider_data) && rider_data.latitude && rider_data.latitude) {
      markers_bound.push([
        Number(rider_data.latitude),
        Number(rider_data.latitude),
      ]);
    }
    if (
      !_.isEmpty(_.get(deliveryInformation, "customer_detail.address", null))
    ) {
      markers_bound.push([
        Number(deliveryInformation?.customer_detail?.address?.latitude),
        Number(deliveryInformation?.customer_detail?.address?.longitude),
      ]);
    }
    if (!_.isEmpty(_.get(deliveryInformation, "sender_detail.address", null))) {
      markers_bound.push([
        Number(deliveryInformation?.sender_detail?.address?.latitude),
        Number(deliveryInformation?.sender_detail?.address?.longitude),
      ]);
    }
    const bounds = L.latLngBounds(markers_bound);
    map_ref.current.fitBounds(bounds);
  };

  useEffect(() => {
    if (_.isEmpty(rider_data)) return;
    const { latitude, longitude } = rider_data;
    if (
      latitude &&
      longitude &&
      map_ref.current &&
      !trip_final_status.includes(
        _.get(deliveryInformation, "fulfillment.status", "")
      )
    ) {
      // calculate_bounds();
      construct_snap_to_road();
    }
  }, [rider_data]);

  const construct_snap_to_road = () => {
    if (waypoint_ref.current)
      map_ref.current.removeControl(waypoint_ref.current);
    waypoint_ref.current = null;

    if (
      configs.show_rider_route &&
      trip_between_status.includes(
        _.get(deliveryInformation, "fulfillment.status", "")
      )
    ) {
      waypoint_ref.current = L.Routing.control({
        waypoints: [
          L.latLng(rider_data?.latitude, rider_data?.longitude),
          L.latLng(
            deliveryInformation?.customer_detail?.address.latitude,
            deliveryInformation?.customer_detail?.address.longitude
          ),
        ],
        lineOptions: {
          styles: [
            { color: theme.colors.lightPurple6, opacity: 0.8, weight: 4 },
          ],
        },
        show: false,
        addWaypoints: false,
        routeWhileDragging: false,
        draggableWaypoints: false,
        fitSelectedRoutes: false,
        showAlternatives: false,
        createMarker: () => null,
      }).addTo(map_ref.current);
    }
  };

  function calculateBearingAngle(lat1, lon1, lat2, lon2) {
    const dLon = (lon2 - lon1) * (Math.PI / 180);

    const y = Math.sin(dLon) * Math.cos(lat2);
    const x =
      Math.cos(lat1) * Math.sin(lat2) -
      Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);

    let bearing = Math.atan2(y, x) * (180 / Math.PI);
    bearing = (bearing + 360) % 360;

    return bearing;
  }

  let angle = 0;

  if (
    !_.isEmpty(rider_data) &&
    rider_data?.latitude &&
    rider_data?.longitude &&
    !trip_final_status.includes(
      _.get(deliveryInformation, "fulfillment.status", "")
    )
  ) {
    angle = calculateBearingAngle(
      deliveryInformation?.customer_detail?.address.latitude,
      deliveryInformation?.customer_detail?.address.longitude,
      rider_data?.latitude,
      rider_data?.longitude
    );
  }
  const pre_pickup_status = ["OUT_FOR_PICKUP", "REACHED_PICKUP", "CREATED"];
  const post_pickup_status = [
    "PICKED_UP",
    "OUT_FOR_DELIVERY",
    "REACHED_DELIVERY",
    "IN_TRANSIT",
  ];
  const ignoreStatuses = ["PICKED_UP", "PICKUP_REJECTED", "PICKUP_FAILED"];

  function calculate_distance(lat1, lon1, lat2, lon2) {
    const EARTH_RADIUS_KM = 6371; // Earth's radius in kilometers
    const to_radians = (degrees) => (degrees * Math.PI) / 180;

    const latitude1 = to_radians(lat1);
    const latitude2 = to_radians(lat2);
    const deltaLatitude = to_radians(lat2 - lat1);
    const deltaLongitude = to_radians(lon2 - lon1);

    const a =
      Math.sin(deltaLatitude / 2) * Math.sin(deltaLatitude / 2) +
      Math.cos(latitude1) *
      Math.cos(latitude2) *
      Math.sin(deltaLongitude / 2) *
      Math.sin(deltaLongitude / 2);
    const centralAngle = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    let distanceInKm = EARTH_RADIUS_KM * centralAngle;
    if (distanceInKm < 1) {
      return (distanceInKm * 1000).toFixed(2);
    } else {
      return distanceInKm.toFixed(2);
    }
  }

  const originalLogs = _.get(deliveryInformation, "fulfillment.logs", []);
  const filteredLogs = useMemo(() => {
    if (!isReverse) return originalLogs;

    return originalLogs.filter((log) => {
      const status = log?.status;

      if (ignoreStatuses.includes(status)) return true;
      return !pickup_statuses.includes(status);
    });
  }, [rider_data, deliveryInformation]);

  const eta_time_text = useMemo(() => {
    const current_time = moment();
    let eta_time = moment();
    let eta_text = "Estimated Delivery";
    let delay_time = "";
    const order_status = _.get(deliveryInformation, "fulfillment.status", "");
    const order_fullfill_info = _.get(deliveryInformation, "fulfillment", "");
    if (_.includes(pre_pickup_status, order_status)) {
      if (order_fullfill_info?.pickup?.eta) {
        const pickup_eta = moment(order_fullfill_info?.pickup?.eta);
        if (pickup_eta.isBefore(current_time)) {
          let time_diff_in_min = moment(moment()).diff(
            moment(order_fullfill_info?.pickup?.eta),
            "minutes"
          );
          if (time_diff_in_min >= 60) {
            const duration = moment.duration(time_diff_in_min, "minutes");
            const hours = Math.floor(duration.asHours());
            const minutes = duration.minutes();
            delay_time = `${hours}:${minutes} hours`;
          } else {
            delay_time = `${time_diff_in_min} minutes`;
          }
        }
        eta_time = pickup_eta;
      } else {
        if (order_fullfill_info?.pickup) {
          const pickup_latitude =
            deliveryInformation?.sender_detail?.address?.latitude;
          const pickup_longitude =
            deliveryInformation?.sender_detail?.address?.longitude;
          const rider_latitude =
            order_fullfill_info?.logs[order_fullfill_info?.logs.length - 1]
              ?.location?.latitude;

          const rider_longitude =
            order_fullfill_info?.logs[order_fullfill_info?.logs.length - 1]
              ?.location?.longitude;
          const distance = calculate_distance(
            pickup_latitude,
            pickup_longitude,
            rider_latitude,
            rider_longitude
          );

          eta_time = moment().add(distance * 2, "minutes");
          if (eta_time.isSame(current_time, "minute")) {
            eta_time = eta_time.add(10, "minutes");
          }
        } else {
          eta_time = current_time.add(10, "minutes");
        }
      }
      eta_text = "Estimated Pickup";
    }

    if (_.includes(post_pickup_status, order_status)) {
      if (order_fullfill_info?.drop?.eta) {
        const drop_eta = moment(order_fullfill_info?.drop?.eta);
        if (drop_eta.isBefore(current_time)) {
          let time_diff_in_min = moment(moment()).diff(
            moment(order_fullfill_info?.drop?.eta),
            "minutes"
          );
          if (time_diff_in_min >= 60) {
            const duration = moment.duration(time_diff_in_min, "minutes");
            const hours = Math.floor(duration.asHours());
            const minutes = duration.minutes();
            delay_time = `${hours}:${minutes} hours`;
          } else {
            delay_time = `${time_diff_in_min} minutes`;
          }
        }
        eta_time = drop_eta;
      } else {
        if (order_fullfill_info?.drop) {
          const drop_latitude =
            deliveryInformation?.customer_detail?.address?.latitude;
          const drop_longitude =
            deliveryInformation?.customer_detail?.address?.longitude;
          const rider_latitude =
            order_fullfill_info?.logs[order_fullfill_info?.logs.length - 1]
              ?.location?.latitude;
          const rider_longitude =
            order_fullfill_info?.logs[order_fullfill_info?.logs.length - 1]
              ?.location?.longitude;
          const distance = calculate_distance(
            drop_latitude,
            drop_longitude,
            rider_latitude,
            rider_longitude
          );
          eta_time = moment().add(distance * 2, "minutes");
          if (eta_time.isSame(current_time, "minute")) {
            eta_time = eta_time.add(10, "minutes");
          }
        } else {
          eta_time = current_time.add(10, "minutes");
        }
      }
      eta_text = "Estimated Delivery";
    }
    const lastLog = filteredLogs[filteredLogs.length - 1];
    if (
      (isReverse && lastLog.status === "PICKED_UP") ||
      lastLog.status === "PICKUP_REJECTED" ||
      lastLog.status == "PICKUP_FAILED"
    ) {
      eta_time = lastLog.timestamp;
      delay_time = "";

      if (
        lastLog.status === "PICKUP_REJECTED" ||
        lastLog.status == "PICKUP_FAILED"
      ) {
        eta_text = lastLog.status === "PICKUP_REJECTED" ? "Pickup Rejected" : "Pickup Failed";
      } else {
        eta_text = "Picked Up";
      }
      return { eta_time, eta_text, delay_time };
    }
    if (order_status == "DELIVERED") {
      if (order_fullfill_info?.drop?.timestamp) {
        eta_time = moment(order_fullfill_info?.drop?.timestamp);
        eta_text = "Delivery Time";
      } else {
        eta_time = "";
        eta_text = "";
      }
    }

    if (
      order_status == "UNDELIVERED" &&
      _.some(order_fullfill_info?.logs, { status: "UNDELIVERED" })
    ) {
      const undelivered_order = _.find(order_fullfill_info?.logs, {
        status: "UNDELIVERED",
      });
      if (undelivered_order?.timestamp) {
        eta_time = moment(undelivered_order?.timestamp);
        eta_text = "Delivery Attempt Time";
      } else {
        eta_time = "";
        eta_text = "";
      }
    }

    if (
      order_status == "RTO_UNDELIVERED" &&
      _.some(order_fullfill_info?.logs, { status: "RTO_UNDELIVERED" })
    ) {
      const rto_undelivered_order = _.find(order_fullfill_info?.logs, {
        status: "RTO_UNDELIVERED",
      });
      if (rto_undelivered_order?.timestamp) {
        eta_time = moment(rto_undelivered_order?.timestamp);
        eta_text = "RTO Delivery Attempt Time";
      } else {
        eta_time = "";
        eta_text = "";
      }
    }

    if (
      order_status == "RTO_DELIVERED" &&
      _.some(order_fullfill_info?.logs, { status: "RTO_DELIVERED" })
    ) {
      const rto_delivered_order = _.find(order_fullfill_info?.logs, {
        status: "RTO_DELIVERED",
      });
      if (rto_delivered_order?.timestamp) {
        eta_time = moment(rto_delivered_order?.timestamp);
        eta_text = "RTO Delivery Time";
      } else {
        eta_time = "";
        eta_text = "";
      }
    }

    if (order_status == "RTO_OUT_FOR_DELIVERY") {
      eta_time = eta_time.add(15, "minutes");
      eta_text = "Estimated RTO Delivery";
    }

    return { eta_time, eta_text, delay_time };
  }, [deliveryInformation, rider_data]);

  return (
    <div
      className="h-100"
      style={
        window.innerWidth < 767
          ? {
            display: "flex",
            flexDirection: "column-reverse",
          }
          : { position: "relative" }
      }
    >
      <div className="h-100 map-content-height">
        <MapContainer
          ref={map_ref}
          whenReady={() => {
            set_map_loaded(true);
          }}
          center={
            !_.isEmpty(rider_data) &&
              rider_data?.latitude &&
              rider_data?.longitude &&
              rider_data?.latitude != "None" &&
              !isNaN(rider_data?.latitude)
              ? {
                lat: Number(rider_data?.latitude),
                lng: Number(rider_data?.longitude),
              }
              : {
                lat: Number(
                  deliveryInformation?.customer_detail?.address?.latitude
                ),
                lng: Number(
                  deliveryInformation?.customer_detail?.address?.longitude
                ),
              }
          }
          zoom={11}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png?key=cb1_2a9x_1_9d5ee2d5adb0987d5bc0824d"
          />
          <Marker
            icon={
              new DivIcon({
                html: `<div>
                  <img src="${_.isEmpty(configs.pickup_marker_image)
                    ? markerGreen
                    : configs.pickup_marker_image
                  }" 
                  width="32px" height="32px"
                  />
                  </div>`,
                iconSize: [0, 0],
              })
            }
            position={[
              deliveryInformation?.sender_detail?.address?.latitude,
              deliveryInformation?.sender_detail?.address?.longitude,
            ]}
          ></Marker>

          {!isReverse && (
            <Marker
              icon={
                new DivIcon({
                  html: `<div>
                  <img src="${_.isEmpty(configs.drop_marker_image)
                      ? markerPurple
                      : configs.drop_marker_image
                    }" 
                  width="32px" height="32px"
                  />
                  </div>`,
                  iconSize: [0, 0],
                })
              }
              position={[
                deliveryInformation?.customer_detail?.address?.latitude,
                deliveryInformation?.customer_detail?.address?.longitude,
              ]}
            ></Marker>
          )}
          {!_.isEmpty(rider_data) &&
            rider_data?.latitude &&
            rider_data?.longitude &&
            rider_data?.latitude != "None" &&
            !isNaN(rider_data?.latitude) &&
            !trip_final_status.includes(
              _.get(deliveryInformation, "fulfillment.status", ""),
            ) &&
            !isPickupFlow && (
              <MovingMarker
                data={{
                  lat: rider_data?.latitude,
                  lng: rider_data?.longitude,
                }}
                icon={
                  new DivIcon({
                    html: `<div style="transform: rotate(${angle}deg) ">
                    <img src="${_.isEmpty(configs.rider_marker_image)
                        ? riderImg
                        : configs.rider_marker_image
                      }" 
                    width="32px" height="32px"
                    />
                    </div>`,
                    iconSize: [0, 0],
                  })
                }
              />
              // <Marker
              //   icon={
              //     new DivIcon({
              //       html: `<div>
              //         <img src="${
              //           _.isEmpty(configs.rider_marker_image)
              //             ? riderImg
              //             : configs.rider_marker_image
              //         }"
              //         width="32px" height="32px"
              //         />
              //         </div>`,
              //       iconSize: [0, 0],
              //     })
              //   }
              //   position={[rider_data?.latitude, rider_data?.longitude]}
              // ></Marker>
            )}
        </MapContainer>

        {_.isEmpty(rider_data) &&
          !trip_final_status.includes(
            _.get(deliveryInformation, "fulfillment.status", ""),
          ) ? (
          <Button
            style={{ fontSize: 14, opacity: 1, zIndex: 1000 }}
            className="p-button-raised submit-btn refresh-rider-location-disabled cursor-unset"
            disabled={true}
            label="Rider Location Unavailable"
          />
        ) : !_.isEmpty(rider_data) &&
          !trip_final_status.includes(
            _.get(deliveryInformation, "fulfillment.status", ""),
          ) ? (
          <Button
            style={{
              fontSize: 14,
              zIndex: 1000,
            }}
            className={`${buttonDisabled
              ? "refresh-rider-location-disabled"
              : "refresh-rider-location-button"
              } p-button-raised submit-btn`}
            label="Refresh Rider Location"
            loading={isLoading}
            disabled={buttonDisabled}
            onClick={() => refreshRiderLocationUsingId(riderId, false)}
            //icon="pi pi-check"
            icon={
              <img src={refresh} alt="refresh" style={{ marginRight: 4 }} />
            }
          />
        ) : null}
      </div>
      <div className="tracking-detail-box w-100">
        <div className="availability-container-content">
          <div className="space-between-container">
            <div>
              {_.has(deliveryInformation, "fulfillment") ? (
                <>
                  <div className="status-details">
                    {eta_time_text?.eta_time != ""
                      ? moment(eta_time_text?.eta_time).format("DD MMM YYYY")
                      : ""}
                    {/* {moment(
                      deliveryInformation.fulfillment.drop?.timestamp
                        ? deliveryInformation.fulfillment.drop.timestamp
                        : deliveryInformation.fulfillment.drop?.eta
                        ? deliveryInformation.fulfillment.drop.eta
                        : deliveryInformation.fulfillment.pickup?.eta
                    ).format("DD MMM YYYY")} */}
                  </div>
                  <h1
                    className={`${isReverse
                      ? filteredLogs?.[filteredLogs.length - 1]?.status ===
                        "PICKED_UP" ||
                        _.get(
                          deliveryInformation,
                          "fulfillment.status",
                          "",
                        ) === "PICKED_UP"
                        ? "color-green"
                        : filteredLogs?.[filteredLogs.length - 1]?.status ===
                          "PICKUP_REJECTED" ||
                          filteredLogs?.[filteredLogs.length - 1]?.status ==
                          "PICKUP_FAILED"
                          ? "color-red"
                          : ""
                      : _.get(
                        deliveryInformation,
                        "fulfillment.status",
                        "",
                      ) === "DELIVERED"
                        ? "color-green"
                        : _.get(
                          deliveryInformation,
                          "fulfillment.status",
                          "",
                        ) === "UNDELIVERED"
                          ? "color-red"
                          : ""
                      } order-information`}
                  >
                    {eta_time_text?.eta_time != ""
                      ? moment(eta_time_text?.eta_time).format("h:mm")
                      : ""}{" "}
                    {/* {moment(
                      deliveryInformation.fulfillment.drop?.timestamp
                        ? deliveryInformation.fulfillment.drop.timestamp
                        : deliveryInformation.fulfillment.drop?.eta
                        ? deliveryInformation.fulfillment.drop.eta
                        : deliveryInformation.fulfillment.pickup?.eta
                    ).format("h:mm")}{" "} */}
                    <span className="timezone-info">
                      {eta_time_text?.eta_time != ""
                        ? moment(eta_time_text?.eta_time).format("a")
                        : ""}
                      {/* {moment(
                        deliveryInformation.fulfillment.drop?.timestamp
                          ? deliveryInformation.fulfillment.drop.timestamp
                          : deliveryInformation.fulfillment.drop?.eta
                          ? deliveryInformation.fulfillment.drop.eta
                          : deliveryInformation.fulfillment.pickup?.eta
                      ).format("a")} */}
                    </span>
                    {/* - 02:00
              <span className="timezone-info"> PM </span> */}
                  </h1>
                </>
              ) : (
                <div className="availability-soon">Available Soon!</div>
              )}

              <div className="status-details">
                {eta_time_text?.eta_text}
                {/* {deliveryInformation.fulfillment.status === "DELIVERED"
                  ? "Delivery Time"
                  : deliveryInformation.fulfillment.status === "UNDELIVERED"
                  ? "Delivery Attempt Time"
                  : deliveryInformation.fulfillment.drop
                  ? "Estimated Delivery"
                  : "Estimated Pickup"} */}
              </div>
            </div>
            <div>
              <Button
                icon={
                  activeIndex && activeIndex.some((index) => index === 0)
                    ? "pi pi-angle-up p-button-icon-right margin-right-none"
                    : "pi pi-angle-down p-button-icon-right margin-right-none"
                }
                label={`${activeIndex && activeIndex.some((index) => index === 0)
                  ? "Hide Logs"
                  : "View Logs"
                  }`}
                disabled={_.isEmpty(
                  _.get(deliveryInformation, "fulfillment.logs", null)
                )}
                onClick={() => onClick(0)}
                style={{ fontSize: 12, boxShadow: "unset", minWidth: 120 }}
                className="p-button-raised p-button-rounded submit-btn pidge-btn-without-shadow"
              />
            </div>
          </div>
          {eta_time_text?.delay_time != "" && (
            <div className="delay-notification">
              <img src={ImageLinks.alert} />
              <Text semi style={{ fontSize: 12, color: theme.colors.red }}>
                Your order is running {eta_time_text?.delay_time} late. Please
                contact the Rider or support for updates
              </Text>
            </div>
          )}
          <ViewDetails
            logs={filteredLogs}
            isReverse={isReverse}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
          />
        </div>
      </div>
    </div>
  );
};

export default TrackingDetails;
